import React from 'react';
import type { EditorElement } from '../../../editor/types';
import type { ToolbarShortcut } from './form-controls/ShortcutableControl';
import { useLocale } from '../../../locales';

interface ToolbarProps {
  selectedElement: EditorElement | null;
  selectedElementIds?: string[];
  elements?: EditorElement[];
  onUndo?: () => void;
  onRedo?: () => void;
  onUpdateElement?: (elementId: string, changes: Partial<EditorElement>) => void;
  onAlignElements?: (elementIds: string[], alignment: "left" | "center" | "right" | "top" | "middle" | "bottom") => void;
  onAlignElementsToCanvas?: (elementIds: string[], alignment: "canvas-left" | "canvas-right" | "canvas-top" | "canvas-bottom" | "canvas-center-h" | "canvas-center-v") => void;
  onGroupElements?: (elementIds: string[]) => void;
  onUngroupElement?: (groupId: string) => void;
  showRulers?: boolean;
  showGuides?: boolean;
  hasGuides?: boolean;
  onToggleRulers?: () => void;
  onToggleGuides?: () => void;
  onAddHorizontalGuide?: () => void;
  onAddVerticalGuide?: () => void;
  onClearGuides?: () => void;
  activeGuidePreset?: "margin-50" | "center" | "columns-3" | "rows-3" | "";
  onApplyGuidePreset?: (preset: "margin-50" | "center" | "columns-3" | "rows-3") => void;
  getShortcutsForElement?: (elementType: string) => ToolbarShortcut[];
}

const Toolbar: React.FC<ToolbarProps> = (props) => {
  const {
    selectedElement,
    selectedElementIds = [],
    elements = [],
    onUndo,
    onRedo,
    onUpdateElement,
    onAlignElements,
    onAlignElementsToCanvas,
    onGroupElements,
    onUngroupElement,
    showRulers = false,
    showGuides = false,
    hasGuides = false,
    onToggleRulers,
    onToggleGuides,
    onAddHorizontalGuide,
    onAddVerticalGuide,
    onClearGuides,
    activeGuidePreset = "",
    onApplyGuidePreset,
    getShortcutsForElement
  } = props;

  const { t } = useLocale();

  const customShortcuts = selectedElement && getShortcutsForElement
    ? getShortcutsForElement(selectedElement.type)
    : [];

  const renderShortcutControl = (shortcut: ToolbarShortcut) => {
    if (!selectedElement || !onUpdateElement) {
      return null;
    }

    const currentValue = getNestedProperty(selectedElement, shortcut.propertyPath);

    const handleChange = (newValue: unknown) => {
      const updates = buildNestedUpdate(selectedElement, shortcut.propertyPath, newValue);
      onUpdateElement(selectedElement.id, updates);
    };

    switch (shortcut.controlType) {
      case 'number': {
        let numericValue: number | '' = '';
        if (typeof currentValue === 'number' && !Number.isNaN(currentValue)) {
          numericValue = currentValue;
        } else if (typeof currentValue === 'string' && currentValue.trim() !== '') {
          const parsed = Number(currentValue);
          numericValue = Number.isNaN(parsed) ? '' : parsed;
        }

        return (
          <div key={shortcut.id} className="qe-toolbar__shortcut">
            <span className="qe-toolbar__shortcut-label" title={shortcut.label}>
              {shortcut.label}
            </span>
            <input
              className="qe-input qe-input--small"
              type="number"
              value={numericValue}
              aria-label={shortcut.label}
              min={shortcut.min}
              max={shortcut.max}
              step={shortcut.step}
              onChange={(event) => {
                const { value } = event.target;
                if (value === '') {
                  handleChange(undefined);
                  return;
                }
                handleChange(Number(value));
              }}
              title={shortcut.label}
            />
          </div>
        );
      }

      case 'color': {
        const fallback = typeof currentValue === 'string' && currentValue.startsWith('#')
          ? currentValue
          : '#000000';

        return (
          <div key={shortcut.id} className="qe-toolbar__shortcut">
            <span className="qe-toolbar__shortcut-label" title={shortcut.label}>
              {shortcut.label}
            </span>
            <input
              className="qe-input qe-input--color qe-input--small"
              type="color"
              value={fallback}
              aria-label={shortcut.label}
              onChange={(event) => handleChange(event.target.value)}
              title={shortcut.label}
            />
          </div>
        );
      }

      case 'select': {
        const value = (currentValue ?? '') as string;

        return (
          <div key={shortcut.id} className="qe-toolbar__shortcut">
            <span className="qe-toolbar__shortcut-label" title={shortcut.label}>
              {shortcut.label}
            </span>
            <select
              className="qe-input qe-input--small"
              value={value}
              aria-label={shortcut.label}
              onChange={(event) => handleChange(event.target.value)}
              title={shortcut.label}
            >
              {shortcut.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      }

      case 'toggle': {
        return (
          <div key={shortcut.id} className="qe-toolbar__shortcut">
            <span className="qe-toolbar__shortcut-label" title={shortcut.label}>
              {shortcut.label}
            </span>
            <input
              className="qe-toolbar__toggle-input"
              type="checkbox"
              checked={!!currentValue}
              onChange={(event) => handleChange(event.target.checked)}
              aria-label={shortcut.label}
              title={shortcut.label}
            />
          </div>
        );
      }

      case 'segmented': {
        const value = currentValue ?? '';

        return (
          <div key={shortcut.id} className="qe-toolbar__shortcut">
            <span className="qe-toolbar__shortcut-label" title={shortcut.label}>
              {shortcut.label}
            </span>
            <div className="qe-toolbar__segment-group">
              {shortcut.options?.map((option) => {
                const isActive = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`qe-toolbar__segment ${isActive ? 'qe-toolbar__segment--active' : ''}`}
                    onClick={() => handleChange(option.value)}
                    aria-label={option.label}
                    title={option.label}
                  >
                    {option.icon ?? option.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      case 'slider': {
        const value = typeof currentValue === 'number' ? currentValue : Number(currentValue) || 0;

        return (
          <div key={shortcut.id} className="qe-toolbar__shortcut">
            <span className="qe-toolbar__shortcut-label" title={shortcut.label}>
              {shortcut.label}
            </span>
            <input
              className="qe-toolbar__slider"
              type="range"
              value={value}
              min={shortcut.min}
              max={shortcut.max}
              step={shortcut.step}
              aria-label={shortcut.label}
              title={shortcut.label}
              onChange={(event) => handleChange(Number(event.target.value))}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  const isMultiSelect = selectedElementIds.length > 1;

  // Check if selected elements contain a group (for ungrouping)
  const selectedGroup = selectedElementIds.length === 1 && elements.find(
    (el) => el.id === selectedElementIds[0] && el.type === "group"
  );

  // Check if any selected element is a group (no nested grouping allowed)
  const hasGroupInSelection = selectedElementIds.some((id) =>
    elements.find((el) => el.id === id && el.type === "group")
  );

  // Can group: multiple items selected and none of them is a group
  const canGroup = isMultiSelect && !hasGroupInSelection;

  return (
    <div className="qe-toolbar">
      {(onUndo || onRedo) && (
        <div className="qe-toolbar__group">
          <span className="qe-toolbar__label">{t("toolbar.edit")}</span>
          <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
            {onUndo && (
              <button
                type="button"
                className="qe-toolbar__button"
                onClick={onUndo}
                title={t("toolbar.undo")}
                aria-label={t("toolbar.undo")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 3L2 7L6 11" />
                  <path d="M2 7H9.5C12 7 14 9 14 11.5" />
                </svg>
              </button>
            )}
            {onRedo && (
              <button
                type="button"
                className="qe-toolbar__button"
                onClick={onRedo}
                title={t("toolbar.redo")}
                aria-label={t("toolbar.redo")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M10 3L14 7L10 11" />
                  <path d="M14 7H6.5C4 7 2 9 2 11.5" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      {(onToggleRulers || onToggleGuides) && (
        <div className="qe-toolbar__group">
          <span className="qe-toolbar__label">{t("toolbar.guides")}</span>
          <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
            {onToggleRulers && (
              <button
                type="button"
                className={`qe-toolbar__button ${showRulers ? "qe-toolbar__button--active" : ""}`}
                onClick={onToggleRulers}
                title={t("toolbar.toggleRulers")}
                aria-label={t("toolbar.toggleRulers")}
                aria-pressed={showRulers}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M2 2H14V14H2V2Z" />
                  <path d="M5 2V5M8 2V4M11 2V5M2 5H5M2 8H4M2 11H5" />
                </svg>
              </button>
            )}
            {onToggleGuides && (
              <button
                type="button"
                className={`qe-toolbar__button ${showGuides ? "qe-toolbar__button--active" : ""}`}
                onClick={onToggleGuides}
                title={t("toolbar.toggleGuides")}
                aria-label={t("toolbar.toggleGuides")}
                aria-pressed={showGuides}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M8 1V15" strokeDasharray="2 2" />
                  <path d="M1 8H15" strokeDasharray="2 2" />
                </svg>
              </button>
            )}
            {onAddVerticalGuide && (
              <button
                type="button"
                className="qe-toolbar__button"
                onClick={onAddVerticalGuide}
                title={t("toolbar.addVerticalGuide")}
                aria-label={t("toolbar.addVerticalGuide")}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M8 2V14" />
                  <path d="M5 5H11M8 2L6 4M8 2L10 4" />
                </svg>
              </button>
            )}
            {onAddHorizontalGuide && (
              <button
                type="button"
                className="qe-toolbar__button"
                onClick={onAddHorizontalGuide}
                title={t("toolbar.addHorizontalGuide")}
                aria-label={t("toolbar.addHorizontalGuide")}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M2 8H14" />
                  <path d="M5 5V11M2 8L4 6M2 8L4 10" />
                </svg>
              </button>
            )}
            {hasGuides && onClearGuides && (
              <button
                type="button"
                className="qe-toolbar__button"
                onClick={onClearGuides}
                title={t("toolbar.clearGuides")}
                aria-label={t("toolbar.clearGuides")}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 4L12 12M12 4L4 12" />
                </svg>
              </button>
            )}
            {onApplyGuidePreset && (
              <label className="qe-toolbar__guide-preset">
                <select
                  className="qe-input qe-input--small"
                  value={activeGuidePreset}
                  aria-label={t("toolbar.guidePresets")}
                  onChange={(event) => {
                    const preset = event.target.value as
                      | "margin-50"
                      | "center"
                      | "columns-3"
                      | "rows-3"
                      | "";
                    if (!preset) {
                      return;
                    }
                    onApplyGuidePreset(preset);
                  }}
                >
                  <option value="">{t("toolbar.guidePresetPlaceholder")}</option>
                  <option value="margin-50">{t("toolbar.guidePresetMargin50")}</option>
                  <option value="center">{t("toolbar.guidePresetCenter")}</option>
                  <option value="columns-3">{t("toolbar.guidePresetColumns3")}</option>
                  <option value="rows-3">{t("toolbar.guidePresetRows3")}</option>
                </select>
              </label>
            )}
          </div>
        </div>
      )}
      {/* Group/Ungroup buttons - shown at the beginning */}
      {(canGroup || selectedGroup) && (
        <div className="qe-toolbar__group">
          <span className="qe-toolbar__label">{t("toolbar.grouping")}</span>
          <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
            {canGroup && onGroupElements && (
              <button
                type="button"
                className="qe-toolbar__button"
                onClick={() => onGroupElements(selectedElementIds)}
                title={t("toolbar.group")}
                aria-label={t("toolbar.group")}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="1" y="1" width="6" height="6" rx="1" />
                  <rect x="9" y="1" width="6" height="6" rx="1" />
                  <rect x="1" y="9" width="6" height="6" rx="1" />
                  <rect x="9" y="9" width="6" height="6" rx="1" />
                  <path d="M7 4 L9 4 M4 7 L4 9 M12 7 L12 9 M7 12 L9 12" strokeWidth="1" strokeDasharray="1 1" />
                </svg>
              </button>
            )}
            {selectedGroup && onUngroupElement && (
              <button
                type="button"
                className="qe-toolbar__button"
                onClick={() => onUngroupElement(selectedGroup.id)}
                title={t("toolbar.ungroup")}
                aria-label={t("toolbar.ungroup")}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="1" y="1" width="6" height="6" rx="1" />
                  <rect x="9" y="1" width="6" height="6" rx="1" />
                  <rect x="1" y="9" width="6" height="6" rx="1" />
                  <rect x="9" y="9" width="6" height="6" rx="1" />
                  <line x1="4" y1="8" x2="12" y2="8" strokeWidth="2" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      {isMultiSelect && onAlignElements && (
        <div className="qe-toolbar__group">
          <span className="qe-toolbar__label">{t("toolbar.alignment")}</span>
          <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElements(selectedElementIds, "left")}
              title={t("toolbar.alignLeft")}
              aria-label={t("toolbar.alignLeft")}
            >
              ⬅
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElements(selectedElementIds, "center")}
              title={t("toolbar.alignCenterH")}
              aria-label={t("toolbar.alignCenterH")}
            >
              ↔
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElements(selectedElementIds, "right")}
              title={t("toolbar.alignRight")}
              aria-label={t("toolbar.alignRight")}
            >
              ➡
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElements(selectedElementIds, "top")}
              title={t("toolbar.alignTop")}
              aria-label={t("toolbar.alignTop")}
            >
              ⬆
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElements(selectedElementIds, "middle")}
              title={t("toolbar.alignCenterV")}
              aria-label={t("toolbar.alignCenterV")}
            >
              ↕
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElements(selectedElementIds, "bottom")}
              title={t("toolbar.alignBottom")}
              aria-label={t("toolbar.alignBottom")}
            >
              ⬇
            </button>
          </div>
        </div>
      )}
      {isMultiSelect && onAlignElementsToCanvas && (
        <div className="qe-toolbar__group">
          <span className="qe-toolbar__label">{t("toolbar.alignToCanvas")}</span>
          <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElementsToCanvas(selectedElementIds, "canvas-left")}
              title={t("toolbar.alignCanvasLeft")}
              aria-label={t("toolbar.alignCanvasLeft")}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="14" height="14" strokeDasharray="2 2" />
                <line x1="2" y1="4" x2="2" y2="12" strokeWidth="2" />
                <line x1="4" y1="6" x2="8" y2="6" />
                <line x1="4" y1="8" x2="7" y2="8" />
                <line x1="4" y1="10" x2="8" y2="10" />
              </svg>
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElementsToCanvas(selectedElementIds, "canvas-center-h")}
              title={t("toolbar.alignCanvasCenterH")}
              aria-label={t("toolbar.alignCanvasCenterH")}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="14" height="14" strokeDasharray="2 2" />
                <line x1="4" y1="8" x2="12" y2="8" strokeWidth="2" />
                <line x1="6" y1="5" x2="6" y2="11" />
                <line x1="8" y1="6" x2="8" y2="10" />
                <line x1="10" y1="5" x2="10" y2="11" />
              </svg>
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElementsToCanvas(selectedElementIds, "canvas-right")}
              title={t("toolbar.alignCanvasRight")}
              aria-label={t("toolbar.alignCanvasRight")}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="14" height="14" strokeDasharray="2 2" />
                <line x1="14" y1="4" x2="14" y2="12" strokeWidth="2" />
                <line x1="8" y1="6" x2="12" y2="6" />
                <line x1="9" y1="8" x2="12" y2="8" />
                <line x1="8" y1="10" x2="12" y2="10" />
              </svg>
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElementsToCanvas(selectedElementIds, "canvas-top")}
              title={t("toolbar.alignCanvasTop")}
              aria-label={t("toolbar.alignCanvasTop")}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="14" height="14" strokeDasharray="2 2" />
                <line x1="4" y1="2" x2="12" y2="2" strokeWidth="2" />
                <line x1="6" y1="4" x2="6" y2="8" />
                <line x1="8" y1="4" x2="8" y2="7" />
                <line x1="10" y1="4" x2="10" y2="8" />
              </svg>
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElementsToCanvas(selectedElementIds, "canvas-center-v")}
              title={t("toolbar.alignCanvasCenterV")}
              aria-label={t("toolbar.alignCanvasCenterV")}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="14" height="14" strokeDasharray="2 2" />
                <line x1="8" y1="2" x2="8" y2="14" strokeWidth="2" />
                <line x1="5" y1="6" x2="11" y2="6" />
                <line x1="6" y1="8" x2="10" y2="8" />
                <line x1="5" y1="10" x2="11" y2="10" />
              </svg>
            </button>
            <button
              type="button"
              className="qe-toolbar__button"
              onClick={() => onAlignElementsToCanvas(selectedElementIds, "canvas-bottom")}
              title={t("toolbar.alignCanvasBottom")}
              aria-label={t("toolbar.alignCanvasBottom")}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="14" height="14" strokeDasharray="2 2" />
                <line x1="4" y1="14" x2="12" y2="14" strokeWidth="2" />
                <line x1="6" y1="8" x2="6" y2="12" />
                <line x1="8" y1="9" x2="8" y2="12" />
                <line x1="10" y1="8" x2="10" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      {selectedElement && customShortcuts.map((shortcut) => renderShortcutControl(shortcut))}
    </div>
  );
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getNestedProperty = (obj: unknown, path: string): any => {
  if (!path) return undefined;
  const keys = path.split('.');
  let current: any = obj;
  for (const key of keys) {
    if (current == null) {
      return undefined;
    }
    current = current[key];
  }
  return current;
};

const buildNestedUpdate = (
  source: EditorElement,
  path: string,
  newValue: unknown,
): Partial<EditorElement> => {
  const keys = path.split('.');
  let update: any = newValue;

  for (let index = keys.length - 1; index >= 0; index -= 1) {
    const key = keys[index];

    if (index === keys.length - 1) {
      update = { [key]: update };
      continue;
    }

    const parentPath = keys.slice(0, index + 1).join('.');
    const parentValue = getNestedProperty(source, parentPath);

    if (Array.isArray(parentValue)) {
      const clone = [...parentValue];
      const childKey = keys[index + 1];
      const childIndex = Number(childKey);
      if (!Number.isNaN(childIndex) && childIndex >= 0) {
        clone[childIndex] = update[childKey];
      }
      update = { [key]: clone };
    } else if (isPlainObject(parentValue)) {
      update = {
        [key]: {
          ...parentValue,
          ...update,
        },
      };
    } else {
      update = {
        [key]: update,
      };
    }
  }

  return update as Partial<EditorElement>;
};

export default Toolbar;
